import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY     = '#bf360c';
const PRIMARY_MID = '#e64a19';
const PRIMARY_LT  = '#fbe9e7';

const fmt = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtQ = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
    if (!d) return '-';
    const s = String(d).split('T')[0];
    const [y, m, day] = s.split('-');
    return `${day}/${m}/${y}`;
};
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const PurchaseReturnAnalysisReport = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate]     = useState(fmt2Date(today));
    const [data, setData]         = useState([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [fetched, setFetched]   = useState(false);
    const [search, setSearch]     = useState('');

    useEffect(() => {
        if (companyId && branchId && token) fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'PurchaseAnalysisReportApi',
                `/PurchaseReturnAnalysisReport/${fromDate}/${toDate}/${companyId}/${branchId}`,
                token
            );
            const rows = res?.response_data || res || [];
            setData(Array.isArray(rows) ? rows : []);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch purchase return analysis report');
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        if (!search.trim()) return data;
        const q = search.toLowerCase();
        return data.filter(r =>
            (r.BillNumber   || '').toLowerCase().includes(q) ||
            (r.SupplierName || '').toLowerCase().includes(q) ||
            (r.ItemName     || '').toLowerCase().includes(q)
        );
    }, [data, search]);

    const totals = useMemo(() => ({
        returnQty:          filtered.reduce((s, r) => s + (parseFloat(r.ReturnQty)          || 0), 0),
        returnAmount:       filtered.reduce((s, r) => s + (parseFloat(r.ReturnAmount)       || 0), 0),
        taxableAmount:      filtered.reduce((s, r) => s + (parseFloat(r.TaxableAmount)      || 0), 0),
        cgst:               filtered.reduce((s, r) => s + (parseFloat(r.CGST)               || 0), 0),
        sgst:               filtered.reduce((s, r) => s + (parseFloat(r.SGST)               || 0), 0),
        igst:               filtered.reduce((s, r) => s + (parseFloat(r.IGST)               || 0), 0),
        freightAmount:      filtered.reduce((s, r) => s + (parseFloat(r.FreightAmount)      || 0), 0),
        freightGSTAmount:   filtered.reduce((s, r) => s + (parseFloat(r.FreightGSTAmount)   || 0), 0),
        packingAmount:      filtered.reduce((s, r) => s + (parseFloat(r.PackingAmount)      || 0), 0),
        packingGSTAmount:   filtered.reduce((s, r) => s + (parseFloat(r.PackingGSTAmount)   || 0), 0),
        tcsAmount:          filtered.reduce((s, r) => s + (parseFloat(r.TCSAmount)          || 0), 0),
        discountAmount:     filtered.reduce((s, r) => s + (parseFloat(r.DiscountAmount)     || 0), 0),
        roundOff:           filtered.reduce((s, r) => s + (parseFloat(r.RoundOff)           || 0), 0),
        grossReturnAmount:  filtered.reduce((s, r) => s + (parseFloat(r.GrossReturnAmount)  || 0), 0),
    }), [filtered]);

    const uniqueBillCount = useMemo(() => {
        const ids = new Set(filtered.map(r => r.PurchaseReturnId));
        return ids.size;
    }, [filtered]);

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'PurchaseReturnAnalysisReport', 0);
        if (!filtered.length) return;
        const headers = ['#', 'Bill No', 'Bill Date', 'Supplier', 'Item', 'Return Qty', 'Return Amt', 'Taxable', 'CGST', 'SGST', 'IGST', 'Freight', 'Freight GST%', 'Freight GST', 'Packing', 'Packing GST%', 'Packing GST', 'TCS', 'Discount', 'Round Off', 'Gross Return Amt'];
        const rows = filtered.map((r, i) => [
            i + 1,
            r.BillNumber || '',
            fmtDate(r.BillDate),
            r.SupplierName || '',
            r.ItemName || '',
            parseFloat(r.ReturnQty)         || 0,
            parseFloat(r.ReturnAmount)      || 0,
            parseFloat(r.TaxableAmount)     || 0,
            parseFloat(r.CGST)              || 0,
            parseFloat(r.SGST)              || 0,
            parseFloat(r.IGST)              || 0,
            parseFloat(r.FreightAmount)     || 0,
            parseFloat(r.FreightGSTRate)    || 0,
            parseFloat(r.FreightGSTAmount)  || 0,
            parseFloat(r.PackingAmount)     || 0,
            parseFloat(r.PackingGSTRate)    || 0,
            parseFloat(r.PackingGSTAmount)  || 0,
            parseFloat(r.TCSAmount)         || 0,
            parseFloat(r.DiscountAmount)    || 0,
            parseFloat(r.RoundOff)          || 0,
            parseFloat(r.GrossReturnAmount) || 0,
        ]);
        const totalRow = ['', '', '', '', 'Totals',
            totals.returnQty, totals.returnAmount, totals.taxableAmount,
            totals.cgst, totals.sgst, totals.igst,
            totals.freightAmount, '', totals.freightGSTAmount,
            totals.packingAmount, '', totals.packingGSTAmount,
            totals.tcsAmount, totals.discountAmount, totals.roundOff,
            totals.grossReturnAmount];

        await exportStyledExcel({
            reportTitle: 'PURCHASE RETURN ANALYSIS REPORT',
            sheetName: 'Purchase Return Analysis',
            fileName: `PurchaseReturnAnalysisReport_${fromDate}_${toDate}.xlsx`,
            companyName,
            username,
            fromDate,
            toDate,
            headers: headers,
            rows,
            totalsRow: totalRow,
            columnWidths: [4, 14, 12, 22, 28, 10, 14, 14, 12, 12, 12, 12, 10, 12, 12, 10, 12, 12, 12, 10, 16],
        });
    };

    const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 10, whiteSpace: 'nowrap', border: `1px solid ${PRIMARY_MID}`, color: '#fff', background: PRIMARY_MID };
    const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };
    const tdR    = { ...tdBase, textAlign: 'right' };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading purchase return analysis report..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Purchase Return Analysis Report
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Item-wise Return Details</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <input
                        placeholder="Search Bill / Supplier / Item..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 200, outline: 'none' }}
                    />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary cards */}
                {fetched && data.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Total Bills',       value: uniqueBillCount,                    color: PRIMARY },
                            { label: 'Return Qty',        value: fmtQ(totals.returnQty),         color: '#374151' },
                            { label: 'Return Amount',     value: fmt(totals.returnAmount),       color: '#0277bd' },
                            { label: 'Taxable',           value: fmt(totals.taxableAmount),      color: '#7b1fa2' },
                            { label: 'CGST',              value: fmt(totals.cgst),               color: '#c62828' },
                            { label: 'SGST',              value: fmt(totals.sgst),               color: '#c62828' },
                            { label: 'IGST',              value: fmt(totals.igst),               color: '#c62828' },
                            { label: 'Gross Return Amt',  value: fmt(totals.grossReturnAmount),  color: '#2e7d32' },
                        ].map(s => (
                            <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90 }}>
                                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>{s.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Meta */}
                {fetched && (
                    <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {fromDate} to {toDate} &nbsp;|&nbsp; {uniqueBillCount} items
                        {search && ` (filtered from ${new Set(data.map(r => r.PurchaseReturnId)).size})`}
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)', scrollbarWidth: 'thin', scrollbarColor: `${PRIMARY} #f0f0f0` }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1800 }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                            <tr style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)` }}>
                                <th style={{ ...thBase, textAlign: 'center', width: 36 }}>#</th>
                                <th style={{ ...thBase, minWidth: 120 }}>Bill No</th>
                                <th style={{ ...thBase, minWidth: 90 }}>Bill Date</th>
                                <th style={{ ...thBase, minWidth: 160 }}>Supplier</th>
                                <th style={{ ...thBase, minWidth: 200 }}>Item</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>Return Qty</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 110 }}>Return Amt</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 110 }}>Taxable</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>CGST</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>SGST</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>IGST</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>Freight</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 80 }}>Frt GST%</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>Frt GST</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>Packing</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 80 }}>Pck GST%</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>Pck GST</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>TCS</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>Discount</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 80 }}>Round Off</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 130, background: '#2e7d32' }}>Gross Return Amt</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={21} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {fetched ? 'No records found' : 'Select date range and click Refresh'}
                                    </td>
                                </tr>
                            ) : (() => {
                                const groups = [];
                                filtered.forEach(r => {
                                    const last = groups[groups.length - 1];
                                    if (last && last.returnId === r.PurchaseReturnId) {
                                        last.rows.push(r);
                                    } else {
                                        groups.push({ returnId: r.PurchaseReturnId, rows: [r] });
                                    }
                                });

                                return groups.map((grp, gi) => {
                                    const bg = gi % 2 === 0 ? '#fff' : PRIMARY_LT;
                                    return grp.rows.map((r, ri) => {
                                        const isFirst = ri === 0;
                                        const span = grp.rows.length;
                                        return (
                                            <tr key={`${r.PurchaseReturnId}-${r.ItemId}-${ri}`} style={{ background: bg }}>
                                                {isFirst && (
                                                    <>
                                                        <td rowSpan={span} style={{ ...tdBase, textAlign: 'center', color: '#94a3b8', verticalAlign: 'middle', borderRight: '2px solid #e2e8f0' }}>{gi + 1}</td>
                                                        <td rowSpan={span} style={{ ...tdBase, fontWeight: 600, color: PRIMARY, verticalAlign: 'middle', borderRight: '2px solid #e2e8f0' }}>{r.BillNumber || '-'}</td>
                                                        <td rowSpan={span} style={{ ...tdBase, color: '#64748b', verticalAlign: 'middle', borderRight: '2px solid #e2e8f0' }}>{fmtDate(r.BillDate)}</td>
                                                        <td rowSpan={span} style={{ ...tdBase, color: '#1e293b', verticalAlign: 'middle', borderRight: '2px solid #e2e8f0' }}>{r.SupplierName || '-'}</td>
                                                    </>
                                                )}
                                                <td style={{ ...tdBase, color: '#374151' }}>{r.ItemName || '-'}</td>
                                                <td style={{ ...tdR, color: '#374151' }}>{fmtQ(r.ReturnQty)}</td>
                                                <td style={{ ...tdR, color: '#0277bd' }}>{fmt(r.ReturnAmount)}</td>
                                                <td style={{ ...tdR, color: '#7b1fa2' }}>{fmt(r.TaxableAmount)}</td>
                                                <td style={{ ...tdR, color: '#c62828' }}>{fmt(r.CGST)}</td>
                                                <td style={{ ...tdR, color: '#c62828' }}>{fmt(r.SGST)}</td>
                                                <td style={{ ...tdR, color: '#c62828' }}>{fmt(r.IGST)}</td>
                                                <td style={{ ...tdR, color: '#6a1b9a' }}>{fmt(r.FreightAmount)}</td>
                                                <td style={{ ...tdR, color: '#6a1b9a' }}>{fmtQ(r.FreightGSTRate)}%</td>
                                                <td style={{ ...tdR, color: '#6a1b9a' }}>{fmt(r.FreightGSTAmount)}</td>
                                                <td style={{ ...tdR, color: '#00695c' }}>{fmt(r.PackingAmount)}</td>
                                                <td style={{ ...tdR, color: '#00695c' }}>{fmtQ(r.PackingGSTRate)}%</td>
                                                <td style={{ ...tdR, color: '#00695c' }}>{fmt(r.PackingGSTAmount)}</td>
                                                <td style={{ ...tdR, color: '#e65100' }}>{fmt(r.TCSAmount)}</td>
                                                <td style={{ ...tdR, color: '#d32f2f' }}>{fmt(r.DiscountAmount)}</td>
                                                <td style={{ ...tdR, color: '#546e7a' }}>{fmt(r.RoundOff)}</td>
                                                <td style={{ ...tdR, fontWeight: 700, color: '#2e7d32' }}>{fmt(r.GrossReturnAmount)}</td>
                                            </tr>
                                        );
                                    });
                                });
                            })()}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, fontWeight: 700, position: 'sticky', bottom: 0 }}>
                                    <td colSpan={5} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#374151' }}>{fmtQ(totals.returnQty)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#0277bd' }}>{fmt(totals.returnAmount)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#7b1fa2' }}>{fmt(totals.taxableAmount)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#c62828' }}>{fmt(totals.cgst)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#c62828' }}>{fmt(totals.sgst)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#c62828' }}>{fmt(totals.igst)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#6a1b9a' }}>{fmt(totals.freightAmount)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#6a1b9a' }}>-</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#6a1b9a' }}>{fmt(totals.freightGSTAmount)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#00695c' }}>{fmt(totals.packingAmount)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#00695c' }}>-</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#00695c' }}>{fmt(totals.packingGSTAmount)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#e65100' }}>{fmt(totals.tcsAmount)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#d32f2f' }}>{fmt(totals.discountAmount)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#546e7a' }}>{fmt(totals.roundOff)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#2e7d32' }}>{fmt(totals.grossReturnAmount)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PurchaseReturnAnalysisReport;
